import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Typography, Table, Button, Tag, Space, message, Breadcrumb, Input, Card, Descriptions, Badge } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import styled from "styled-components";

import { WHITE } from "../../constant/colors";
import { getPatientDetailsAPI } from "../../api/api";

const { Title, Text } = Typography;

const PageContainer = styled.div`
  width: 98%;
  margin: 0 auto;
  padding: 24px;
  background: ${WHITE};
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ConsentPreferencesManagement = () => {
  const [patientSearchId, setPatientSearchId] = useState("");
  const [patientData, setPatientData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handlePatientSearch = async () => {
    if (!patientSearchId) return;
    setLoading(true);
    try {
      const { data } = await getPatientDetailsAPI(patientSearchId);
      setPatientData(data);
    } catch (error) {
      message.error("Failed to fetch patient data");
      setPatientData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <Breadcrumb>
        <Breadcrumb.Item>
          <Link to="/settings">Settings</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Consent & Preferences</Breadcrumb.Item>
      </Breadcrumb>

      <div style={{ margin: "16px 0" }}>
        <Title level={3} style={{ margin: 0 }}>
          Consent & Preference Management
        </Title>
        <Text type="secondary">
          View and manage patient opt-in/opt-out preferences. Search for a patient to see their consent details and
          communication settings.
        </Text>
      </div>

      <Card title="Consent Categories Overview" size="small" style={{ marginBottom: 16 }}>
        <Table
          size="small"
          pagination={false}
          dataSource={[
            {
              key: "hipaa",
              category: "HIPAA Authorization",
              description: "Use and disclosure of Protected Health Information",
              defaultStatus: "Required"
            },
            {
              key: "tcpa",
              category: "TCPA Marketing",
              description: "Receive calls and text messages about products/services",
              defaultStatus: "Opt-in"
            },
            {
              key: "email_marketing",
              category: "Email Marketing",
              description: "Receive marketing emails",
              defaultStatus: "Opt-in"
            },
            {
              key: "sms_notifications",
              category: "SMS Notifications",
              description: "Receive SMS notifications about prescriptions/reminders",
              defaultStatus: "Opt-in"
            },
            {
              key: "inapp_notifications",
              category: "In-App Notifications",
              description: "Receive in-app popup notifications",
              defaultStatus: "Opt-in"
            }
          ]}
          columns={[
            { title: "Category", dataIndex: "category", key: "category", width: 200 },
            { title: "Description", dataIndex: "description", key: "description" },
            {
              title: "Default",
              dataIndex: "defaultStatus",
              key: "default",
              width: 100,
              render: (status) => <Tag color={status === "Required" ? "red" : "green"}>{status}</Tag>
            }
          ]}
        />
      </Card>

      <Card title="Patient Consent Lookup" size="small">
        <Space style={{ marginBottom: 16 }}>
          <Input
            placeholder="Enter Patient ID or MRN"
            value={patientSearchId}
            onChange={(e) => setPatientSearchId(e.target.value)}
            onPressEnter={handlePatientSearch}
            style={{ width: 250 }}
          />
          <Button icon={<SearchOutlined />} loading={loading} onClick={handlePatientSearch}>
            Search
          </Button>
        </Space>

        {patientData && (
          <div>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Patient ID">{patientData.id}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Badge
                  status={patientData.generalConsent?.status === "active" ? "success" : "error"}
                  text={patientData.generalConsent?.status || "No consent"}
                />
              </Descriptions.Item>
              <Descriptions.Item label="HIPAA Consent">
                {patientData.generalConsent?.hipaa ? (
                  <Tag color="green">Active</Tag>
                ) : (
                  <Tag color="red">Not consented</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="TCPA Consent">
                {patientData.generalConsent?.tcpa ? (
                  <Tag color="green">Active</Tag>
                ) : (
                  <Tag color="red">Not consented</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Opted-Out Categories" span={2}>
                {patientData.generalConsent?.optedOutCategories?.length > 0 ? (
                  patientData.generalConsent.optedOutCategories.map((cat) => (
                    <Tag key={cat} color="orange">
                      {cat}
                    </Tag>
                  ))
                ) : (
                  <Text type="secondary">None</Text>
                )}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Card>
    </PageContainer>
  );
};

export { ConsentPreferencesManagement };
